import { authApi } from '@/lib/api';
import { ExternalLinkIcon } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Landing screen for unauthenticated users.
 *
 * Single call-to-action: kick off the GitLab OAuth dance. Clicking "Continue
 * with GitLab" sets `window.location` to the backend's `/auth/gitlab/login`
 * which redirects to the GitLab instance's consent page. After the user
 * authorizes, GitLab redirects to our `/auth/gitlab/callback`, which sets the
 * session cookie and finally lands the browser back on the frontend's
 * `WEB_ORIGIN`.
 *
 * We deliberately do NOT use `fetch` here: the OAuth flow must happen as a
 * full top-level navigation so the browser follows GitLab's 302s and keeps
 * the user's address bar coherent with where the consent form lives.
 *
 * Layout follows the PMS design system "login-card" (approximately 420px
 * wide, logo centered, brand lockup "patrickmathieu · singularité" as a
 * footer caption).
 */
export function LoginScreen() {
  const handleSignIn = () => {
    window.location.href = authApi.gitlabLoginUrl();
  };

  return (
    <section className="mx-auto flex w-full max-w-md flex-col items-center gap-6 pt-12 text-center">
      <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {/*
          Decorative mark: the "patrickmathieu singularité" signature
          below (the brand lockup) already conveys authorship semantically,
          so the logo image is treated as presentational. Empty `alt` +
          `aria-hidden` is the canonical pattern — either alone works with
          modern AT, but combining them is belt-and-braces against older
          screen readers that don't fully honour `aria-hidden` on <img>.
        */}
        <img src="/logo.png" alt="" className="mx-auto mb-5 h-16 w-16" aria-hidden="true" />
        <h1 className="font-bold text-[22px] text-brand-900 tracking-tight">Hills</h1>
        <p className="mt-1.5 text-balance text-slate-500 text-sm">
          Shape Up hill charts for GitLab. Track your pitches with dots on a hill - figuring it out,
          cresting, shipping.
        </p>

        <Button size="lg" onClick={handleSignIn} className="mt-6 w-full justify-center gap-2">
          <ExternalLinkIcon className="h-4 w-4" />
          Continue with GitLab
        </Button>

        <p className="mt-4 text-slate-500 text-xs">
          We only read your GitLab projects and milestones - no write access.
        </p>

        {/*
          Brand signature — mirrors the "patrickmathieu singularité" lockup
          from the charter, using weight contrast instead of colour so it
          stays a quiet footer rather than competing with the CTA.
        */}
        <p className="mt-5 text-slate-500 text-xs">
          by <span className="font-bold text-slate-700">patrickmathieu</span>
          <span className="ml-[2px] font-extralight text-slate-500">{'\u00A0singularité'}</span>
        </p>
      </div>
    </section>
  );
}
